class RRSlideshowMedia extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.carousel = this.closest('slideshow-carousel');
    if (!this.carousel) { this.abort = null; return; }
    this.userPaused ??= false;
    this.control = this.carousel.querySelector('[data-rr-video-control]');
    this.videos = [...this.carousel.querySelectorAll('video-media[autoplay][type="video"] video')];
    this.visible = new Set();
    this.suspended = new Set();
    const options = { signal: this.abort.signal };
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRect.width && entry.intersectionRect.height) this.visible.add(entry.target);
        else this.visible.delete(entry.target);
      }
      this.update();
    });
    for (const video of this.videos) {
      this.observer.observe(video);
      video.addEventListener('play', () => this.update(), options);
      video.addEventListener('pause', () => this.updateControl(), options);
      video.addEventListener('ended', () => this.updateControl(), options);
    }
    this.control?.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.togglePlayback();
    }, options);
    customElements.whenDefined('slideshow-carousel').then(() => {
      if (!this.abort?.signal.aborted && this.isConnected) this.bindPlayer();
    });
    for (const event of ['carousel:change', 'carousel:settle']) this.carousel.addEventListener(event, () => this.update(), options);
    document.addEventListener('visibilitychange', () => this.update(), options);
    window.addEventListener('pagehide', () => this.suspend(), options);
    window.addEventListener('pageshow', () => this.update(), options);
    this.updateControl();
  }

  activeVideo() {
    return this.videos.find(video => video.closest('.slideshow__slide')?.classList.contains('is-selected')
      && video.getClientRects().length > 0);
  }

  updateControl() {
    if (!this.control) return;
    const video = this.activeVideo();
    this.control.hidden = !video;
    if (!video) return;
    const paused = this.userPaused || video.paused;
    this.control.dataset.paused = String(paused);
    this.control.setAttribute('aria-label', paused ? this.control.dataset.playLabel : this.control.dataset.pauseLabel);
    this.control.querySelector('[data-rr-video-pause]').toggleAttribute('hidden', paused);
    this.control.querySelector('[data-rr-video-play]').toggleAttribute('hidden', !paused);
  }

  bindPlayer() {
    const player = this.carousel.player;
    if (!player || this.boundPlayer === player) return;
    this.boundPlayer = player;
    for (const type of ['player:start', 'player:resume', 'player:visibility-resume']) {
      player.addEventListener(type, () => {
        if (this.userPaused) player.stop();
      }, { signal: this.abort.signal });
    }
    if (this.userPaused) player.stop();
  }

  togglePlayback() {
    const video = this.activeVideo();
    if (!video) return;
    this.userPaused = !(this.userPaused || video.paused);
    if (this.userPaused) {
      this.carousel.player?.stop();
      this.suspended.clear();
    } else {
      for (const item of this.videos) this.suspended.add(item);
      this.carousel.player?.start();
      if (this.carousel.hasAttribute('autoplay-pause-on-video')) this.carousel.player?.pause();
    }
    this.update();
  }

  update() {
    this.bindPlayer();
    for (const video of this.videos) {
      const active = !this.userPaused && !document.hidden && this.visible.has(video) && video.closest('.slideshow__slide')?.classList.contains('is-selected');
      if (!active) {
        if (!video.paused) {
          this.suspended.add(video);
          video.pause();
        }
      } else if (this.suspended.delete(video)) {
        video.play().catch(() => {});
      }
    }
    this.updateControl();
  }

  suspend() {
    for (const video of this.videos) {
      if (!video.paused) {
        this.suspended.add(video);
        video.pause();
      }
    }
  }

  disconnectedCallback() {
    this.suspend();
    this.abort?.abort();
    this.abort = null;
    this.boundPlayer = null;
    this.observer?.disconnect();
  }
}

if (!customElements.get('rr-slideshow-media')) customElements.define('rr-slideshow-media', RRSlideshowMedia);
